import { Autocomplete, CircularProgress, Stack, TextField } from "@mui/material";
import { useDebounce } from "@src/appV2/lib/utils";
import { FormFieldController } from "@src/appV2/PlacementCandidates/components/FormFieldController";
import { useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { Text } from "@src/lib/ui-react";
import { useState } from "react";
import { type Control } from "react-hook-form";

import { type RequirementMappingFormValues } from "../types";

interface HCFRequiredBySelectFieldProps {
  hcfId?: string;
  control: Control<RequirementMappingFormValues>;
}

export function HCFRequiredBySelectField({ hcfId, control }: HCFRequiredBySelectFieldProps) {
  const [searchInput, setSearchInput] = useState<string>("");
  const debouncedSearchInput = useDebounce({
    debounceTimeInMs: 300,
    value: searchInput,
  });

  const { data: facilitiesData, isFetching } = useSearchFacilities(
    {
      searchInput: debouncedSearchInput,
    },
    {
      // FIXME: Automatically disabled during ESLint config update
      enabled: Boolean(debouncedSearchInput),
    }
  );

  const hcfOptions =
    facilitiesData?.pages
      .flatMap((page) => page.list)
      .map((facility) => ({
        id: facility.userId,
        name: facility.name,
      })) ?? [];

  let noOptionsTextValue = "Start typing to search for workplaces";

  if (isFetching) {
    noOptionsTextValue = "Searching workplaces...";
  } else if (debouncedSearchInput && hcfOptions.length === 0) {
    noOptionsTextValue = "No workplaces found";
  }

  return (
    <FormFieldController
      required
      name="requiredByHCFs"
      control={control}
      label="Workplace"
      sx={{ mb: 2 }}
      render={(field) => (
        <Autocomplete
          multiple
          freeSolo
          autoComplete
          options={hcfOptions}
          // FIXME: Automatically disabled during ESLint config update
          disabled={Boolean(hcfId)}
          loading={isFetching}
          loadingText="Searching workplaces..."
          value={field.value}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }

            return option.name;
          }}
          getOptionKey={(option) => (typeof option === "string" ? option : option.id)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options) => options}
          renderOption={(renderProps, option) => {
            if (!option) {
              return null;
            }

            return (
              <li {...renderProps} key={option.id}>
                <Stack direction="column">
                  <Text>{option.name}</Text>
                  <Text variant="body2" color="textSecondary">
                    WorkplaceID: {option.id}
                  </Text>
                </Stack>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Start typing workplace name..."
              // @ts-expect-error - field not typed correctly
              // FIXME: Automatically disabled during ESLint config update
              error={Boolean(field.error)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isFetching && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={noOptionsTextValue}
          onInputChange={(_event, newValue) => {
            setSearchInput(newValue);
          }}
          onChange={(_, newValue) => {
            field.onChange(newValue);
          }}
          onBlur={field.onBlur}
        />
      )}
    />
  );
}
